/**
 * Procedural shader actions — operator-defined, shader-driven idle
 * motion (vertex displacement) that runs without a skeleton. Designed
 * for mascot / Q-version characters whose GLB has no
 * `THREE.AnimationClip` to play.
 *
 * Schema is a discriminated union keyed on `type` so additional motion
 * styles (sway, pulse, float, jiggle, …) can be added without breaking
 * existing records. Each entry's `params` is type-specific and
 * range-clamped via zod so out-of-range slider values never reach the
 * runtime.
 */
import { z } from 'zod';
const ProceduralActionAxisSchema = z.enum(['x', 'y', 'z']);
/**
 * Breath — vertical (or selected-axis) sine-wave displacement on the
 * upper part of the model, masked by normalized height. Defaults are
 * tuned to produce a gentle, clearly visible breath on a 1m-tall
 * character.
 */
export const BreathActionSchema = z.object({
    type: z.literal('breath'),
    id: z.string().uuid(),
    label: z.string().min(1).max(40),
    enabled: z.boolean().default(true),
    params: z.object({
        /** Peak displacement (model-local units). 0.012 ≈ 1.2cm on a 1m model. */
        strength: z.number().min(0).max(0.05).default(0.012),
        /** Sine frequency in rad/s. 2.4 ≈ a slow exhale every 2.6s. */
        speed: z.number().min(0.1).max(8).default(2.4),
        /** Fractional height (0=bottom, 1=top) below which displacement is zero. */
        maskStart: z.number().min(0).max(1).default(0.20),
        /** Mask fade width above `maskStart`. Larger = softer transition. */
        maskSoftness: z.number().min(0).max(0.6).default(0.25),
        /** Displacement axis in model-local space. */
        axis: ProceduralActionAxisSchema.default('y'),
    }),
});
/**
 * Sway — horizontal pendulum lean. A sine-wave displacement on a
 * horizontal axis (x or z) whose magnitude GROWS with normalized height
 * (feet planted, head/upper-body sways most), giving a gentle "swaying
 * in a breeze" idle distinct from breath's vertical rise/fall. Skeleton-
 * free; complements breath (the two stack on the same model).
 */
export const SwayActionSchema = z.object({
    type: z.literal('sway'),
    id: z.string().uuid(),
    label: z.string().min(1).max(40),
    enabled: z.boolean().default(true),
    params: z.object({
        /** Peak horizontal displacement at the top of the model (model-local units). */
        amplitude: z.number().min(0).max(0.08).default(0.02),
        /** Sine frequency in rad/s. 1.2 ≈ a slow lean every ~5s. */
        speed: z.number().min(0.1).max(6).default(1.2),
        /** Horizontal sway axis in model-local space (x = side-to-side, z = front-back). */
        axis: z.enum(['x', 'z']).default('x'),
        /** Fractional height below which displacement is zero (planted base). */
        maskStart: z.number().min(0).max(1).default(0.1),
        /** Mask fade width above `maskStart`. Larger = softer planted-to-sway transition. */
        maskSoftness: z.number().min(0).max(0.6).default(0.3),
    }),
});
export const ProceduralActionSchema = z.discriminatedUnion('type', [
    BreathActionSchema,
    SwayActionSchema,
]);
/** Cap per character — see proposal D7. */
export const PROCEDURAL_ACTIONS_MAX = 8;
/** Cache-key stable identifier per type+version. Bump when shader patch changes. */
export const PROCEDURAL_TYPE_VERSIONS = {
    breath: 'breath-v1',
    sway: 'sway-v1',
};
